import React from 'react';
import {
  INICIAL_DEL_DIA,
  NOMBRE_DEL_DIA,
  ORDEN_DE_LA_SEMANA,
  diaAnterior,
  diaDeHoy,
  diaSiguiente,
} from '../utils/semana';

interface WeekdaySelectorProps {
  /** Día mostrado ahora mismo (0 domingo … 6 sábado), o `null` si se permite. */
  value: number | null;
  onChange: (dayOfWeek: number | null) => void;
  /** Añade «Sin día» al ciclo, antes del lunes. */
  allowNone?: boolean;
  /** Días de esta semana en los que ya se ha entrenado. */
  entrenados?: ReadonlySet<number>;
  /** Marca cuál es hoy. Solo tiene sentido en la rutina del gimnasio. */
  marcarHoy?: boolean;
  /** Versión pequeña, para ir dentro de una tarjeta. */
  compact?: boolean;
  /** Nombre de lo que se está moviendo, para las etiquetas de accesibilidad. */
  label?: string;
  disabled?: boolean;
}

/**
 * El día de la semana: la semana entera de lunes a domingo, con una flecha a
 * cada lado.
 *
 * Aquí no se elige un día del calendario —cada lunes es el mismo lunes— sino
 * el día de la semana. Los siete están a la vista para poder ir a uno de un
 * toque y, sobre todo, para ver la semana de un vistazo: qué días llevas
 * entrenados y dónde estás. Las flechas siguen valiendo para moverse de uno en
 * uno, que es el gesto cómodo en el móvil.
 *
 * El orden es el de la semana, de lunes a domingo, y el ciclo da la vuelta por
 * los dos extremos: del domingo, «siguiente» lleva al lunes.
 *
 * En `compact` no cabe la tira —va dentro de la tarjeta de cada rutina, y su
 * ciclo incluye «Sin día»—, así que ahí se queda en las dos flechas.
 */
const WeekdaySelector: React.FC<WeekdaySelectorProps> = ({
  value,
  onChange,
  allowNone = false,
  entrenados,
  marcarHoy = false,
  compact = false,
  label,
  disabled = false,
}) => {
  const hoy = diaDeHoy();
  const esHoy = marcarHoy && value === hoy;
  const hecho = value !== null && (entrenados?.has(value) ?? false);

  // Con «Sin día» el ciclo tiene ocho posiciones y `null` es la primera.
  const mover = (haciaDelante: boolean): number | null => {
    if (!allowNone) {
      const actual = value ?? ORDEN_DE_LA_SEMANA[0];
      return haciaDelante ? diaSiguiente(actual) : diaAnterior(actual);
    }
    const ultimo = ORDEN_DE_LA_SEMANA[ORDEN_DE_LA_SEMANA.length - 1];
    if (value === null) return haciaDelante ? ORDEN_DE_LA_SEMANA[0] : ultimo;
    if (haciaDelante) return value === ultimo ? null : diaSiguiente(value);
    return value === ORDEN_DE_LA_SEMANA[0] ? null : diaAnterior(value);
  };

  const texto = value === null ? 'Sin día' : NOMBRE_DEL_DIA[value];
  const conNombre = (que: string) => (label ? `${label}, ${que}` : que);

  const flecha = (haciaDelante: boolean) => (
    <button
      type="button"
      disabled={disabled}
      aria-label={conNombre(haciaDelante ? 'día siguiente' : 'día anterior')}
      onClick={() => onChange(mover(haciaDelante))}
      className={`shrink-0 rounded-lg text-[#cbd5e1] bg-[#334155] hover:bg-[#475569] disabled:opacity-40 ${
        compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-base'
      }`}
    >
      {haciaDelante ? '›' : '‹'}
    </button>
  );

  const valor = (
    <span
      aria-live="polite"
      className={`text-center text-white font-semibold ${
        compact ? 'text-xs min-w-[5.5rem]' : 'text-lg min-w-[8rem]'
      }`}
    >
      {texto}
      {hecho && <span className="text-[#6ee06f]"> ✓</span>}
    </span>
  );

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1">
        {flecha(false)}
        {valor}
        {flecha(true)}
      </span>
    );
  }

  // La inicial no se lee sola («X» por miércoles), así que cada día lleva su
  // nombre en el aria-label, y el punto de entrenado va también ahí.
  const tiraDeLaSemana = (
    <div
      role="radiogroup"
      aria-label="Día de la semana"
      className="flex flex-1 min-w-0 items-center gap-0.5 sm:gap-1"
    >
      {ORDEN_DE_LA_SEMANA.map((dia) => {
        const activo = dia === value;
        const entrenado = entrenados?.has(dia) ?? false;
        return (
          <button
            key={dia}
            type="button"
            role="radio"
            aria-checked={activo}
            aria-label={
              entrenado ? `${NOMBRE_DEL_DIA[dia]}, entrenado` : NOMBRE_DEL_DIA[dia]
            }
            disabled={disabled}
            onClick={() => onChange(dia)}
            className={[
              'flex flex-1 min-w-0 flex-col items-center gap-0.5',
              'rounded-md py-1.5 text-xs sm:text-sm font-semibold',
              'transition-colors outline-none focus:ring-2 focus:ring-[#1f9e3f]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              activo
                ? 'bg-[#1f9e3f] text-white shadow-sm'
                : 'text-[#cbd5e1] hover:bg-[#334155]',
              // Hoy se marca con un borde, que no compite con el día elegido.
              marcarHoy && dia === hoy && !activo ? 'ring-1 ring-[#475569]' : '',
            ].join(' ')}
          >
            <span aria-hidden="true">{INICIAL_DEL_DIA[dia]}</span>
            <span
              aria-hidden="true"
              className={`h-1 w-1 rounded-full ${
                entrenado ? (activo ? 'bg-white' : 'bg-[#6ee06f]') : 'bg-transparent'
              }`}
            />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="bg-[#1e293b] rounded-xl border border-[#334155] p-3 sm:p-4 mb-4">
      <div className="flex items-center gap-1 sm:gap-2">
        {flecha(false)}
        {tiraDeLaSemana}
        {flecha(true)}
      </div>

      <div className="flex flex-col items-center mt-2">
        {valor}
        <span className="text-[10px] uppercase tracking-wider text-[#64748b] mt-0.5">
          {esHoy ? 'hoy' : `hoy es ${NOMBRE_DEL_DIA[hoy].toLowerCase()}`}
        </span>
      </div>

      {!esHoy && (
        <div className="mt-3 text-center">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(hoy)}
            className="text-xs text-[#6ee06f] hover:underline disabled:opacity-40"
          >
            Volver a hoy
          </button>
        </div>
      )}

      {entrenados && entrenados.size > 0 && (
        <p className="text-[#64748b] text-xs mt-3 text-center">
          Llevas {entrenados.size}{' '}
          {entrenados.size === 1 ? 'día entrenado' : 'días entrenados'} esta
          semana.
        </p>
      )}
    </div>
  );
};

export default WeekdaySelector;
